import type {
    ActivityHighlights,
    ApiUsage,
    ApiUsageQuery,
    DashboardExposureSummary,
    DashboardWatch,
    EmployeeRiskByDepartment,
    EmployeeRiskStats,
} from '~/types/business';
import { pbRequest, pbRequestRaw } from '~/services/request';

// Read-only dashboard and analytics aggregates, the company's API usage, and
// the Athena-backed company activity log.
export const analyticsService = {
    /**
     * Calls made with this company's credentials over an inclusive range of UTC
     * days, bucketed by day or month, in total and per credential.
     *
     * Gated on the same `api` read grant as the credential list, so a 403 here
     * is the caller's role and not a failure to retry. A range wider than 366
     * days, or an end before its start, is a 422.
     */
    async getApiUsage(companyId: number, query: ApiUsageQuery): Promise<ApiUsage> {
        return await pbRequest<ApiUsage>(`/companies/${companyId}/api-usage`, {
            query: { ...query },
            failure: 'Failed to fetch API usage',
        });
    },

    /**
     * Get dashboard activity highlights
     */
    async getActivityHighlights(companyId: number): Promise<ActivityHighlights> {
        return await pbRequest<ActivityHighlights>(`/companies/${companyId}/dashboard/activity`, {
            failure: 'Failed to fetch activity highlights',
        });
    },

    /**
     * Get dashboard company exposure summary — aggregate exposure counts,
     * per-surface category magnitudes, and removal progress.
     */
    async getDashboardExposureSummary(companyId: number): Promise<DashboardExposureSummary> {
        return await pbRequest<DashboardExposureSummary>(`/companies/${companyId}/dashboard/exposure-summary`, {
            failure: 'Failed to fetch exposure summary',
        });
    },

    /**
     * The shape of the company's watch over time: the two series, the cadence the
     * licenses run at, and when the roster was last re-checked. Deliberately narrow,
     * because every other watch figure is already on the exposure summary above.
     */
    async getDashboardWatch(companyId: number): Promise<DashboardWatch> {
        return await pbRequest<DashboardWatch>(`/companies/${companyId}/dashboard/watch`, {
            failure: 'Failed to fetch the watch',
        });
    },

    /**
     * Get employee risk data totals
     */
    async getEmployeeRiskData(companyId: number): Promise<any> {
        return await pbRequest<any>(`/companies/${companyId}/analytics/employee-risk-data`, {
            failure: 'Failed to fetch employee risk data',
        });
    },

    /**
     * Get employees over risk score threshold
     */
    async getEmployeesOverRiskScore(companyId: number): Promise<any> {
        return await pbRequest<any>(`/companies/${companyId}/analytics/employees-over-risk`, {
            failure: 'Failed to fetch employees over risk',
        });
    },

    /**
     * Get most at risk employees
     */
    async getMostAtRiskEmployees(companyId: number): Promise<any[]> {
        return await pbRequest<any[]>(`/companies/${companyId}/analytics/most-at-risk-employees`, {
            failure: 'Failed to fetch most at risk employees',
        });
    },

    /**
     * Get employee risk statistics for dashboard charts
     * Returns current stats for high/medium/low risk employees
     */
    async getEmployeeRiskStats(companyId: number): Promise<EmployeeRiskStats> {
        return await pbRequest<EmployeeRiskStats>(`/companies/${companyId}/analytics/employees-risk-stats`, {
            failure: 'Failed to fetch employee risk stats',
        });
    },

    /**
     * Each department's employee count and average risk, one row per department by id.
     */
    async getEmployeeRiskByDepartment(companyId: number): Promise<EmployeeRiskByDepartment[]> {
        return await pbRequest<EmployeeRiskByDepartment[]>(`/companies/${companyId}/analytics/employee-risk-by-department`, {
            failure: 'Failed to fetch employee risk by department',
        });
    },

    /**
     * Get recent activity
     */
    async getRecentActivity(companyId: number): Promise<any[]> {
        return await pbRequest<any[]>(`/companies/${companyId}/dashboard/recent-activity`, {
            failure: 'Failed to fetch recent activity',
        });
    },

    /**
     * Get employee analytics
     */
    async getEmployeeAnalytics(companyId: number): Promise<any> {
        return await pbRequest<any>(`/companies/${companyId}/analytics/employees`, {
            failure: 'Failed to fetch employee analytics',
        });
    },

    /**
     * Athena-backed activity log scoped to a company's employees. Returns the
     * raw event stream (timestamp / actor / method / route / status) — no
     * server-side sort or text-search beyond what the Athena query supports
     * (date range + method filter + route substring). Page-side controls do
     * the rest.
     */
    async getActivityLog(
        companyId: number,
        params?: {
            start_date?: string;
            end_date?: string;
            methods?: string[];
            route?: string;
            section?: string;
            user_id?: number;
            page?: number;
            per_page?: number;
        }
    ): Promise<{
        data: Array<{
            user_id: number | null;
            actor_name: string | null;
            actor_email: string | null;
            created_at: string | null;
            method: string | null;
            route: string | null;
            status: number | null;
            duration_ms: number | null;
            request_id: string | null;
            payload_size: number | null;
            response_size: number | null;
        }>;
        meta: { page: number; per_page: number; count: number; has_more: boolean; truncated: boolean };
    }> {
        const query: Record<string, unknown> = {};
        if (params?.start_date) query.start_date = params.start_date;
        if (params?.end_date) query.end_date = params.end_date;
        if (params?.methods?.length) query.methods = params.methods.join(',');
        if (params?.route) query.route = params.route;
        if (params?.section) query.section = params.section;
        if (params?.user_id) query.user_id = params.user_id;
        if (params?.page) query.page = params.page;
        if (params?.per_page) query.per_page = params.per_page;

        const envelope = await pbRequestRaw<{
                data: any[];
                meta: { page: number; per_page: number; count: number; has_more: boolean; truncated: boolean };
            }>(
            `/companies/${companyId}/activity-log`,
            {
                method: 'GET',
                params: query,
                failure: 'Failed to fetch activity log',
            }
        );

        return {
            data: envelope.data ?? [],
            meta: envelope.meta ?? { page: 1,
            per_page: 0, count: 0, has_more: false, truncated: false },
        };
    },
};
